<template>
  <div>
    <div
      id="side-navigation"
      :style="{ transform: 'translate(' + slideMenu.xPosition + 'px, 0)' }"
      :class="{ 'o-side-navigation--transition': slideMenu.enableTransition }"
      class="o-side-navigation"
      @touchstart="slideStart"
      @touchmove="slideMove"
      @touchend="slideStop">
      <v-list
        class="o-side-navigation__list"
        :list="menu"
        data-test="side-navigation-list"
        @click.native="slideToggle"></v-list>
      <div class="o-side-navigation__menu-buttons" v-if="this.$slots.navButtons">
        <slot name="navButtons"></slot>
      </div>
    </div>
    <div
      :style="{
        'background': 'rgba(0, 0, 0, ' + slideMenu.overlayAlpha +')',
        'pointer-events': slideMenu.overlayAlpha === 0 ? 'none' : 'auto',
      }"
      class="o-side-navigation__overlay"
      :class="{ 'o-side-navigation--transition': slideMenu.enableTransition }"
      @click="slideToggle" />
  </div>
</template>

<script>
import { VButton } from '../atoms';
import { VList } from '../molecules';

export default {
  name: 'v-side-navigation',
  props: {
    menu: {
      required: true,
      type: [Array],
    },
    menuClick: {
      required: false,
      type: [Object, Function],
      default: () => ({}),
    },
  },
  components: {
    VButton,
    VList,
  },
  data() {
    return {
      slideMenu: {
        triggerDistance: 40,
        menuWidth: 240,
        overlayAlphaTarget: 0.7,
        xTouchStart: null,
        xTouchMove: null,
        xTouchStop: null,
        xStartPosition: null,
        xPosition: -240,
        enableTransition: true,
        overlayAlpha: 0,
      },
    };
  },
  mounted() {
    this.slideMenu.menuWidth = document.getElementById('side-navigation').offsetWidth;
    this.slideMenu.xPosition = -this.slideMenu.menuWidth;

    this.$bus.$on('hideSideNavigation', () => {
      this.slideHide();
    });
  },
  methods: {
    slideStart(e) {
      document.documentElement.style.overflow = 'hidden';
      this.slideMenu.enableTransition = false;
      this.slideMenu.xStartPosition = this.slideMenu.xPosition;
      this.slideMenu.xTouchStart = e.changedTouches[0].clientX;
    },
    slideMove(e) {
      this.slideMenu.xTouchMove = e.changedTouches[0].clientX;
      const xStart = this.slideMenu.xStartPosition;
      const xDifference = this.slideMenu.xTouchMove - this.slideMenu.xTouchStart;
      if ((xStart + xDifference) < -this.slideMenu.menuWidth) {
        this.slideMenu.xPosition = -this.slideMenu.menuWidth;
      } else if ((xStart + xDifference) >= 0) {
        this.slideMenu.xPosition = 0;
      } else {
        this.slideMenu.xPosition = xStart + xDifference;
      }
      this.slideMenu.overlayAlpha = 0.7 - ((this.slideMenu.xPosition / -this.slideMenu.menuWidth) * 0.7); // eslint-disable-line max-len
    },
    slideStop(e) { // Calculate which state to fall into (open / close)
      this.slideMenu.enableTransition = true;
      this.slideMenu.xTouchStop = e.changedTouches[0].clientX;
      const xFinalDifference = this.slideMenu.xTouchStop - this.slideMenu.xTouchStart;
      if (xFinalDifference > this.slideMenu.triggerDistance) {
        this.slideMenu.xPosition = 0;
        this.slideMenu.overlayAlpha = 0.7;
      } else if ((xFinalDifference < -this.slideMenu.triggerDistance) || (xFinalDifference < this.slideMenu.triggerDistance && xFinalDifference > -this.slideMenu.triggerDistance && this.slideMenu.xStartPosition < -(this.slideMenu.menuWidth / 2))) { // eslint-disable-line max-len
        this.slideMenu.xPosition = -this.slideMenu.menuWidth;
        this.slideMenu.overlayAlpha = 0;
        document.documentElement.style.overflow = 'auto';
      } else {
        this.slideMenu.xPosition = 0;
        this.slideMenu.overlayAlpha = 0.7;
      }
    },
    slideToggle() {
      if (this.slideMenu.xPosition > -this.slideMenu.menuWidth) {
        this.slideMenu.xPosition = -this.slideMenu.menuWidth;
        this.slideMenu.overlayAlpha = 0;
        document.documentElement.style.overflow = 'auto';
      } else {
        this.slideMenu.xPosition = 0;
        this.slideMenu.overlayAlpha = 0.7;
        document.documentElement.style.overflow = 'hidden';
      }
    },
    slideHide() {
      this.slideMenu.xPosition = -this.slideMenu.menuWidth;
      this.slideMenu.overlayAlpha = 0;
      document.documentElement.style.overflow = 'auto';
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../styles/component-utils';

  $parent: '.o-side-navigation';

  #{$parent} {
    display: flex;
    flex-flow: column;
    width: 240px;
    height: calc(100% - 84px);
    background: $signable-secondary-2;
    position: absolute;
    color: #ffffff;
    overflow-x: hidden;

    &__list {
      flex: 1;
      overflow-y: auto;
    }

    &__overlay {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 4;
      background: rgba(0,0,0,0);
    }

    &__menu-buttons {
      width: calc(100%);
      background: $signable-secondary-2-shade-1;
      padding: $spacing-base;
      bottom: 0;
      box-shadow: 0px -3px 5px 0px rgba(0, 0, 0, 0.15);
      .a-button {
        margin-top: 15px;
        width: 100%;
        &:nth-of-type(1) {
          margin-top: 0;
        }
      }
    }

    &--transition {
      transition-property: transform, background-color, background;
      transition-duration: 0.4s;
      transition-timing-function: ease-out;
    }

    @media (min-width: 1021px) {
      transform: translate(0, 0) !important;
    }

    @media (max-width: 1020px) {
      transform: translate(-240px, 0);
      z-index: 6;

      &__overlay {
        display: block;
      }
    }

    @media (max-width: 1020px) and (min-width: 451px) {
      top: 84px;
      &__overlay {
        top: 84px;
      }
    }

    @media (max-width: 450px) {
      position: fixed;
      z-index: 102;
      top: 54px;
      height: calc(100% - 54px);
      &__overlay {
        top: 54px;
        z-index: 101;
      }
    }
  }

</style>
